import React from 'react';

// Utility methods for the Personal Records feature
import Localizer from 'localizer';
import Personalizer from 'personalizer';

import DateParser from 'utils/date/DateParser';
import DateFormatter from 'utils/date/DateFormatter';
import DurationValidator from 'utils/validators/DurationValidator';
import NumberValidator from 'utils/validators/NumberValidator';
import ActivityTypeMapper from 'utils/ActivityTypeMapper';
import PersonalRecordTypeMapper from 'backbone/utils/PersonalRecordTypeMapper';

import { Types, GetSortOrder } from 'collections/prs/PersonalRecordTypes';
import NumberKeyDown from 'components/NumberKeyDown';

export const PersonalRecordTabTypes = {
    STEPS: 'STEPS',
    WALKING: 'WALKING',
    CYCLING: 'CYCLING',
    RUNNING: 'RUNNING',
    SWIMMING: 'SWIMMING',
    TRAINING: 'TRAINING',
    WHEELCHAIR_PUSH_WALK: 'WHEELCHAIR_PUSH_WALK',
    WHEELCHAIR_PUSH_RUN: 'WHEELCHAIR_PUSH_RUN'
};

export const PUSHES_STREAKS_IDS = [36, 37];
export const STEPS_STREAKS_IDS = [15, 16];

export const mapSportToIcon = function(sport) {
    switch (sport) {
        case 'RUNNING':
            return 'icon-activity-running';
        case 'CYCLING':
            return 'icon-activity-cycling';
        case 'SWIMMING':
            return 'icon-activity-swimming';
        case 'WHEELCHAIR_PUSH_WALK':
            return 'icon-activity-wheelchair-push-walk';
        case 'WHEELCHAIR_PUSH_RUN':
            return 'icon-activity-wheelchair-push-walk';
        default:
            return 'icon-steps-1';
    }
};

export const typesComparator = (a, b) => {
    var aSortOrder = GetSortOrder(a.key);

    var bSortOrder = GetSortOrder(b.key);

    if (aSortOrder < bSortOrder) {
        return -1;
    } else if (aSortOrder > bSortOrder) {
        return 1;
    } else {
        return 0;
    }
};

export const getPrDate = (prDateLong, prType, prValue) => {
    let headline = '';
    let prDate = DateParser.parseISO(prDateLong);
    let startDate;
    let endDate;
    switch (prType) {
        case Types.STEPS_BEST_DAY:
        case Types.PUSHES_BEST_DAY:
            headline = DateFormatter.formatMonthDayYear(prDate);
            break;

        case Types.STEPS_BEST_WEEK:
        case Types.PUSHES_BEST_WEEK:
            let dow = prDate.getDay();
            if (dow == 0) {
                dow = 7;
            }
            let first = prDate.getDate() - dow + 1; // First day is the day of the month - the day of the week.  adjust to Monday start of week
            let last = first + 6; // last day is the first day + 6

            startDate = new Date(prDate);
            startDate.setDate(first);
            endDate = new Date(prDate);
            endDate.setDate(last);

            headline = formatPRDateRange(startDate, endDate);
            break;

        case Types.STEPS_BEST_MONTH:
        case Types.PUSHES_BEST_MONTH:
            headline = DateFormatter.formatMonthYear(prDate);
            break;

        case Types.STEPS_LONGEST_STREAK:
        case Types.STEPS_CURRENT_STREAK:
        case Types.PUSHES_LONGEST_STREAK:
        case Types.PUSHES_CURRENT_STREAK:
            startDate = new Date(prDate);

            let nonNumIndex = prValue ? [...prValue].findIndex((v) => isNaN(v)) : -1;
            prValue = nonNumIndex !== -1 ? prValue.slice(0, nonNumIndex) : prValue || 0;
            startDate.setDate(startDate.getDate() - (prValue - 1));
            endDate = new Date(prDate);

            if (prValue == 0) {
                headline = '--';
            } else {
                if (prValue == 1) {
                    headline = DateFormatter.formatMonthDayYear(prDate);
                } else {
                    headline = formatPRDateRange(startDate, endDate);
                }
            }
            break;

        default:
            headline = DateFormatter.formatMonthDayYear(prDate);
            break;
    }

    return headline;
};

const formatPRDateRange = (startDate, endDate) => {
    return DateFormatter.formatDateRange(startDate, endDate);
};

export const getKeyDownByType = (componentRef, type, children) => {
    let keyPressHandler = <>{children}</>;

    switch (type) {
        case Types.RUN_FARTHEST:
        case Types.CYCLE_FARTHEST:
        case Types.SWIM_LONGEST:
        case Types.CYCLE_MAX_ELEV:
        case Types.CYCLE_MAX_POWER:
            keyPressHandler = <NumberKeyDown componentRef={componentRef} typeFormat={'decimal'} children={children} />;
            break;
        case Types.RUN_1K:
        case Types.RUN_5K:
        case Types.RUN_10K:
        case Types.RUN_HALF_MARATHON:
        case Types.RUN_MARATHON:
        case Types.CYCLE_40K:
        case Types.SWIM_100M:
        case Types.SWIM_400M:
        case Types.SWIM_750M:
        case Types.SWIM_1000M:
        case Types.SWIM_1500M:
        case Types.SWIM_100YD:
        case Types.SWIM_500YD:
        case Types.SWIM_1000YD:
        case Types.SWIM_1650YD:
            keyPressHandler = <>{children}</>;
            break;
        default:
            keyPressHandler = <>{children}</>;
    }

    return keyPressHandler;
};

export const validateInput = (type, elementRef) => {
    let validator;
    const value = elementRef.current.innerText;

    switch (type) {
        case Types.RUN_FARTHEST:
        case Types.CYCLE_FARTHEST:
            validator = new NumberValidator({
                element: elementRef.current,
                required: true,
                mustBePositive: true,
                minValue: 0,
                maxValue: 160934400.07,
                conversionFunction: (val) => {
                    let unit =
                        Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC
                            ? 'kilometer'
                            : 'mile';
                    return Personalizer.convertDistanceToCm(val, unit);
                }
            });

            if (validator.validateInputNow()) {
                return Localizer.localize('validation.activity.distance.invalid', value);
            }

            break;
        case Types.SWIM_LONGEST:
            validator = new NumberValidator({
                element: elementRef.current,
                required: true,
                mustBePositive: true,
                minValue: 0,
                maxValue: 160934400.07,
                conversionFunction: (val) => {
                    let unit =
                        Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC
                            ? 'meter'
                            : 'yard';
                    return Personalizer.convertDistanceToCm(val, unit);
                }
            });

            if (validator.validateInputNow()) {
                return Localizer.localize('validation.activity.distance.invalid', value);
            }
            break;
        case Types.RUN_1K:
        case Types.RUN_5K:
        case Types.RUN_10K:
        case Types.RUN_HALF_MARATHON:
        case Types.RUN_MARATHON:
        case Types.CYCLE_40K:
        case Types.SWIM_100M:
        case Types.SWIM_400M:
        case Types.SWIM_750M:
        case Types.SWIM_1000M:
        case Types.SWIM_1500M:
        case Types.SWIM_100YD:
        case Types.SWIM_500YD:
        case Types.SWIM_1000YD:
        case Types.SWIM_1650YD:
            validator = new DurationValidator({ required: true, element: elementRef.current });

            if (validator.validateInputNow()) {
                return Localizer.localize('pr.time.format.maxValue', value);
            }

            break;

        case Types.CYCLE_MAX_ELEV:
            validator = new NumberValidator({
                element: elementRef.current,
                required: true,
                mustBePositive: true,
                minValue: 0,
                maxValue: 30479.6953,
                conversionFunction: (value) => {
                    if (Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC) {
                        return value;
                    } else {
                        // convert ft to meters
                        return value * (1 / Personalizer.ConversionFactors.MetersToFeet);
                    }
                }
            });

            if (validator.validateInputNow()) {
                let unit =
                    Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC
                        ? Localizer.localize('elevation_unit_metric')
                        : Localizer.localize('elevation_unit_statute');
                let maxValue =
                    Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC
                        ? '30,479'
                        : '99,999';
                return Localizer.localize('pr.edit.error.message', maxValue, unit);
            }
            break;

        case Types.CYCLE_MAX_POWER:
            validator = new NumberValidator({
                element: elementRef.current,
                required: true,
                mustBePositive: true,
                minValue: 0,
                maxValue: 999
            });

            if (validator.validateInputNow()) {
                return Localizer.localize('pr.power.maxValue');
            }
            break;
    }

    return;
};

export const getDefaultSportByActivityType = (activityType) => {
    let defaultSport;
    switch (activityType) {
        case ActivityTypeMapper.Running:
            defaultSport = PersonalRecordTabTypes.RUNNING;
            break;
        case ActivityTypeMapper.Cycling:
            defaultSport = PersonalRecordTabTypes.CYCLING;
            break;
        case ActivityTypeMapper.LapSwimming: // Pool Swimming
            defaultSport = PersonalRecordTabTypes.SWIMMING;
            break;
        case ActivityTypeMapper.StrengthTraining:
            defaultSport = PersonalRecordTabTypes.TRAINING;
            break;
        case ActivityTypeMapper.WheelchairPushWalk:
            defaultSport = PersonalRecordTabTypes.WHEELCHAIR_PUSH_WALK;
            break;
        case ActivityTypeMapper.WheelchairPushRun:
            defaultSport = PersonalRecordTabTypes.WHEELCHAIR_PUSH_RUN;
            break;
        default:
            defaultSport = PersonalRecordTabTypes.STEPS; // steps
            break;
    }

    return defaultSport;
};

export const filterTypes = (type, personalRecordTypes) => {
    let prTypes = [];

    if (personalRecordTypes && personalRecordTypes.length > 0) {
        prTypes = personalRecordTypes.filter((t) => {
            //Some values/keys that are sent from API are not supposed to be/are treated on web - we need to filter them out CA-88063
            // issue fix for ticket CA-114902
            let isTypeInPropTypes = PersonalRecordTypeMapper.getRecordTypeFromKey(t.key);

            return t.sport === type && isTypeInPropTypes;
        });
    }

    prTypes.sort(typesComparator);

    return prTypes;
};
